import React, { memo, useCallback } from 'react';
import { Typography, Avatar, Zoom } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { DateTime } from 'luxon';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import { getLuxonJsDateTimeFormatFromMe } from 'utils/date-time';
import { useMeContext } from 'modules/me';
import { SyncError } from './hooks';

const useStyles = makeStyles((theme) => ({
  lastUpdateWrapper: {
    display: 'flex',
    flexDirection: 'column'
  },

  title: {
    marginBottom: theme.spacing(-0.5),
    marginLeft: theme.spacing(0.5),
    color: '#bbb'
  },
  lastSyncValue: {
    marginLeft: theme.spacing(0.5),
    position: 'relative'
  },
  lastSyncErrorContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    transform: 'scale(1) translate(110%, -10%)'
  },
  lastSyncErrors: {
    color: theme.palette.syncBackgroundStatus.white,
    backgroundColor: theme.palette.secondary.main,
    height: theme.spacing(3),
    width: theme.spacing(3),
    cursor: 'pointer'
  }
}));

type LastSyncedTimeProps = {
  lastSyncTime?: DateTime;
  lastSyncErrors: SyncError[];
  setOpenErrorDialog: (open: boolean) => void;
};

export const LastSyncedTime: React.FC<LastSyncedTimeProps> = memo(
  ({
    lastSyncTime,
    lastSyncErrors,
    setOpenErrorDialog
  }: LastSyncedTimeProps) => {
    const classes = useStyles();
    const me = useMeContext();
    const format = getLuxonJsDateTimeFormatFromMe(me);
    const onClick = useCallback(() => setOpenErrorDialog(true), [
      setOpenErrorDialog
    ]);

    return (
      <span className={classes.lastUpdateWrapper}>
        <Typography
          data-testid="last-updated-title"
          className={classes.title}
          variant="subtitle2"
        >
          <FormattedMessage id="smartDiary.lastSyncTime" />
        </Typography>
        <Typography
          data-testid="last-updated-text"
          className={classes.lastSyncValue}
          variant="subtitle2"
        >
          {lastSyncTime ? (
            lastSyncTime.toFormat(format)
          ) : (
            <FormattedMessage id="smartDiary.lastSyncNotAvailable" />
          )}
          <div className={classes.lastSyncErrorContainer}>
            <Zoom in={lastSyncErrors.length > 0} mountOnEnter unmountOnExit>
              <Avatar
                data-testid="last-sync-errors-notification-avatar"
                onClick={onClick}
                className={classes.lastSyncErrors}
                sizes="small"
              >
                <NotificationImportantIcon fontSize="small" />
              </Avatar>
            </Zoom>
          </div>
        </Typography>
      </span>
    );
  }
);

LastSyncedTime.displayName = 'LastSyncedTime';
